import React, { useState, useRef, useEffect } from 'react'
import { Container } from '@mui/material'
import { useReasoningTraces } from '@/services/reasoning-traces/reasoning-traces.service'
import { useDatasources } from '@/services/datasources/datasources.service'
import Button from '@/components/ui/button'
import Input from '@/components/ui/input'
import {
  StudioWrapper,
  ChatSection,
  TracesSection,
  DatasourcesBar,
  ChatMessages,
  ChatInputWrapper,
  Message,
  TracesList,
  TraceItem,
  DatasourceChip,
  ChatHeader,
  TracesHeader,
  LoadingIndicator,
  EmptyState,
} from './styled'

interface ChatMessage {
  id: string
  content: string
  sender: 'user' | 'assistant'
  timestamp: Date
  reasoningTraceId?: string
}

const Studio: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      content:
        'Hello! I can help you analyze financial data and run backtests. What would you like to explore today?',
      sender: 'assistant',
      timestamp: new Date(Date.now() - 3600000),
    },
  ])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const { data: tracesData } = useReasoningTraces({ page: 1, limit: 5 })
  const { data: datasourcesData } = useDatasources({ page: 1, limit: 3 })

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content: inputValue,
      sender: 'user',
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    setInputValue('')
    setIsLoading(true)

    // Simulate AI response
    setTimeout(() => {
      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: `I'll analyze that for you. Let me process your request: "${inputValue}"`,
        sender: 'assistant',
        timestamp: new Date(),
        reasoningTraceId: tracesData?.traces[0]?.session_id,
      }
      setMessages((prev) => [...prev, assistantMessage])
      setIsLoading(false)
    }, 1500)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  // Get specific datasources for the studio
  const studioDatasources = datasourcesData?.datasources.slice(0, 3) || []

  return (
    <Container maxWidth={false} sx={{ padding: 0, height: '100vh' }}>
      <StudioWrapper>
        <ChatSection>
          <ChatHeader>
            <h3>AI Assistant</h3>
            <span>Financial Analysis & Backtesting</span>
          </ChatHeader>

          <ChatMessages>
            {messages.map((message) => (
              <Message key={message.id} sender={message.sender}>
                <div className="message-content">
                  {message.content}
                  {message.reasoningTraceId && (
                    <div className="trace-link">
                      <a
                        href={`/reasoning-traces/${message.reasoningTraceId}`}
                        target="_blank"
                      >
                        View reasoning trace →
                      </a>
                    </div>
                  )}
                </div>
                <div className="message-time">
                  {formatTime(message.timestamp)}
                </div>
              </Message>
            ))}
            {isLoading && (
              <LoadingIndicator>
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </LoadingIndicator>
            )}
            <div ref={messagesEndRef} />
          </ChatMessages>

          <ChatInputWrapper>
            <Input
              placeholder="Ask about market data, run backtests, or analyze strategies..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              style={{ flex: 1 }}
            />
            <Button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              variant="primary"
            >
              Send
            </Button>
          </ChatInputWrapper>
        </ChatSection>

        <TracesSection>
          <DatasourcesBar>
            <h4>Connected Data Sources</h4>
            <div className="datasources-list">
              <DatasourceChip connected>
                <span className="status-dot"></span>
                KambaData Demo Snowflake
              </DatasourceChip>
              <DatasourceChip connected>
                <span className="status-dot"></span>
                EDGAR API
              </DatasourceChip>
              <DatasourceChip connected>
                <span className="status-dot"></span>
                Brain Sentiment Analysis Product
              </DatasourceChip>
            </div>
          </DatasourcesBar>

          <TracesHeader>
            <h3>Recent Reasoning Traces</h3>
            <Button
              variant="secondary"
              onClick={() => window.open('/reasoning-traces', '_blank')}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              View All
            </Button>
          </TracesHeader>

          <TracesList>
            {tracesData?.traces.length === 0 ? (
              <EmptyState>
                <p>No reasoning traces yet</p>
                <span>Start a conversation to see AI reasoning</span>
              </EmptyState>
            ) : (
              tracesData?.traces.map((trace) => (
                <TraceItem
                  key={trace.session_id}
                  status={trace.status}
                  onClick={() =>
                    window.open(
                      `/reasoning-traces/${trace.session_id}`,
                      '_blank',
                    )
                  }
                >
                  <div className="trace-header">
                    <h4>{trace.title}</h4>
                    <span className={`status ${trace.status}`}>
                      {trace.status}
                    </span>
                  </div>
                  <div className="trace-meta">
                    <span>
                      Started: {new Date(trace.started_at).toLocaleTimeString()}
                    </span>
                    <span>•</span>
                    <span>{trace.phases.length} phases</span>
                  </div>
                  {trace.summary && (
                    <div className="trace-stats">
                      <span>{trace.summary.total_outputs} outputs</span>
                      <span>{trace.summary.total_files} files</span>
                    </div>
                  )}
                </TraceItem>
              ))
            )}
          </TracesList>
        </TracesSection>
      </StudioWrapper>
    </Container>
  )
}

export default Studio
